import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  ViewChild,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { INaviRouteMarker, INaviRouteStep } from "./navi-route-map.model";

/** Drawing surface. Every coordinate below lives in this space, so the map scales with its box. */
const VIEWBOX_WIDTH = 1000;
const VIEWBOX_HEIGHT = 240;
const PADDING_X = 90;
const MID_Y = 132;
/** Nudges the first stops up and down so the route reads as a road rather than a chart line. */
const WAVE_Y = [0, -14, 8, -16, 4];
/** Total climb from the first stop to the last one. */
const RISE = 64;
/** How finely the path is sampled when looking for the length of a stop. */
const SAMPLES = 400;

let instanceCounter = 0;

interface IPoint {
  x: number;
  y: number;
}

interface ISegment {
  id: string;
  d: string;
  fromKey: string;
  toKey: string;
}

/**
 * The NaviDerece route: a winding road whose segments carry the colour of the level they lead to,
 * with a pulsing stop per level, a travelling marker and the position of whoever is being followed.
 *
 * Nothing here knows about students or exams — the caller passes the stops, which stop is current
 * and how far it is to the next one. That keeps the same map usable for a student's level journey
 * today and for a date-based comparison inside the NaviDerece module later.
 */
@Component({
  selector: "app-navi-route-map",
  templateUrl: "./navi-route-map.component.html",
  styleUrl: "./navi-route-map.component.scss",
})
export class NaviRouteMapComponent implements OnChanges, AfterViewInit {
  @Input() steps: INaviRouteStep[] = [];
  /** Index of the stop the traveller has reached. */
  @Input() activeIndex = 0;
  /** How far along the segment towards the next stop, 0-100. */
  @Input() progressToNext = 0;
  @Input() marker?: INaviRouteMarker | null;
  /** The logo that keeps driving along the road; purely decorative. */
  @Input() showNavigator = true;
  @Input() navigatorIcon = "assets/logo/gks-dark-symbol.svg";
  @Input() ariaLabel = "";

  @ViewChild("routePath") routePath?: ElementRef<SVGPathElement>;

  readonly viewBoxWidth = VIEWBOX_WIDTH;
  readonly viewBoxHeight = VIEWBOX_HEIGHT;
  readonly uid = `navi-route-${++instanceCounter}`;

  points: IPoint[] = [];
  segments: ISegment[] = [];
  routeD = "";
  /** Where the position pin sits; measured from the rendered path, so browser only. */
  pin: IPoint | null = null;

  reduceMotion = false;

  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.reduceMotion = this.isBrowser && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  ngOnChanges(): void {
    this.buildRoute();
    // The pin can only be measured once the new path is in the DOM.
    if (this.isBrowser) {
      setTimeout(() => this.updatePin());
    }
  }

  ngAfterViewInit(): void {
    // Setting `pin` here, inside the first check of this view, would change a bound value after
    // it was checked (NG0100); measuring on the next tick lets the change land in its own pass.
    if (this.isBrowser) {
      setTimeout(() => this.updatePin());
    }
  }

  pathId(): string {
    return `${this.uid}-path`;
  }

  glowId(): string {
    return `${this.uid}-glow`;
  }

  markerColorKey(): string {
    const index = Math.max(0, Math.min(this.activeIndex, this.steps.length - 1));
    return this.steps[index]?.levelKey ?? "default";
  }

  /** Percentage positions of the bubble, so it follows the pin at any width. */
  bubbleLeft(): number {
    return this.pin ? (this.pin.x / VIEWBOX_WIDTH) * 100 : 0;
  }

  bubbleTop(): number {
    return this.pin ? ((this.pin.y - 56) / VIEWBOX_HEIGHT) * 100 : 0;
  }

  private buildRoute() {
    this.points = this.stopPoints(this.steps.length);
    this.routeD = this.fullPath(this.points);
    this.segments = this.points.slice(0, -1).map((point, index) => ({
      id: `${this.uid}-seg-${index}`,
      d: this.segmentPath(point, this.points[index + 1]),
      fromKey: this.steps[index]?.levelKey ?? "default",
      toKey: this.steps[index + 1]?.levelKey ?? "default",
    }));
  }

  /** Stops spread evenly across the width, climbing steadily and waving a little on the way. */
  private stopPoints(count: number): IPoint[] {
    if (count < 2) {
      return count === 1 ? [{ x: VIEWBOX_WIDTH / 2, y: MID_Y }] : [];
    }

    const span = VIEWBOX_WIDTH - PADDING_X * 2;
    return Array.from({ length: count }, (_, index) => ({
      x: PADDING_X + (span * index) / (count - 1),
      y: MID_Y + RISE / 2 - (RISE * index) / (count - 1) + (WAVE_Y[index] ?? 0),
    }));
  }

  private segmentPath(from: IPoint, to: IPoint): string {
    const dx = (to.x - from.x) * 0.5;
    return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
  }

  private fullPath(points: IPoint[]): string {
    if (!points.length) {
      return "";
    }

    return points.reduce((path, point, index) => {
      if (index === 0) {
        return `M ${point.x} ${point.y}`;
      }

      const previous = points[index - 1];
      const dx = (point.x - previous.x) * 0.5;
      return `${path} C ${previous.x + dx} ${previous.y}, ${point.x - dx} ${point.y}, ${point.x} ${point.y}`;
    }, "");
  }

  /**
   * Walks the rendered path to find how far along the traveller is: the length of the current stop
   * plus the share of the segment that leads to the next one.
   */
  private updatePin() {
    const path = this.routePath?.nativeElement;
    if (!this.isBrowser || !path || !path.getTotalLength || this.points.length < 2) {
      this.pin = null;
      return;
    }

    const total = path.getTotalLength();
    const stopLengths = this.points.map((stop) => this.lengthOfPoint(path, total, stop));

    let traveled: number;
    if (this.activeIndex >= this.points.length - 1) {
      traveled = total;
    } else {
      const index = Math.max(0, Math.min(this.activeIndex, this.points.length - 2));
      const segmentStart = stopLengths[index];
      const segmentEnd = stopLengths[index + 1];
      const progress = Math.min(100, Math.max(0, this.progressToNext)) / 100;
      traveled = segmentStart + (segmentEnd - segmentStart) * progress;
    }

    const point = path.getPointAtLength(traveled);
    this.pin = { x: point.x, y: point.y };
  }

  /** Nearest point on the path to a stop, sampled — curves have no closed-form inverse. */
  private lengthOfPoint(path: SVGPathElement, total: number, stop: IPoint): number {
    let bestLength = 0;
    let bestDistance = Infinity;

    for (let i = 0; i <= SAMPLES; i++) {
      const length = (total * i) / SAMPLES;
      const point = path.getPointAtLength(length);
      const distance = (point.x - stop.x) ** 2 + (point.y - stop.y) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        bestLength = length;
      }
    }

    return bestLength;
  }
}
